import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DollarSign, CheckCircle, AlertCircle, Eye, EyeOff, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  passwordSchema as sharedPasswordSchema,
  PASSWORD_REQUIREMENTS,
  isObviouslyWeakPassword,
  mapSupabasePasswordError,
  isSessionError,
} from "@/lib/passwordSchema";

const passwordSchema = z
  .object({
    password: sharedPasswordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [invalidReason, setInvalidReason] = useState("Este link expirou ou é inválido. Solicite um novo.");
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingRecovery, setCheckingRecovery] = useState(true);
  const [warning, setWarning] = useState<{ title: string; description: string } | null>(null);
  const hasResolvedRecoveryRef = useRef(false);
  const hasExchangedCodeRef = useRef(false);
  const validationTimeoutRef = useRef<number | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const alertRef = useRef<HTMLDivElement | null>(null);

  const showWarning = (title: string, description: string) => {
    setWarning({ title, description });
    setPassword("");
    setConfirmPassword("");
    setTimeout(() => {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      passwordInputRef.current?.focus();
    }, 50);
  };

  useEffect(() => {
    let isMounted = true;
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const hasRecoveryContext =
      queryParams.get("type") === "recovery" ||
      hashParams.get("type") === "recovery" ||
      queryParams.get("recovery") === "1" ||
      queryParams.has("code") ||
      queryParams.has("access_token") ||
      queryParams.has("token_hash") ||
      hashParams.has("access_token") ||
      hashParams.has("token_hash");

    console.info("[ResetPassword] init", {
      hasRecoveryContext,
      hasCode: queryParams.has("code"),
      hasQueryAccessToken: queryParams.has("access_token"),
      hasHashAccessToken: hashParams.has("access_token"),
      hasTokenHash: queryParams.has("token_hash") || hashParams.has("token_hash"),
      recoveryFlag: queryParams.get("recovery"),
    });

    const clearValidationTimeout = () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    };

    const markRecoveryReady = () => {
      if (!isMounted || hasResolvedRecoveryRef.current) return;

      hasResolvedRecoveryRef.current = true;
      console.info("[ResetPassword] recovery ready");
      clearValidationTimeout();
      setSessionReady(true);
      setInvalidLink(false);
      setCheckingRecovery(false);
    };

    const markRecoveryInvalid = (reason: string) => {
      if (!isMounted || hasResolvedRecoveryRef.current) return;

      hasResolvedRecoveryRef.current = true;
      console.warn("[ResetPassword] recovery invalid", { reason });
      clearValidationTimeout();
      setSessionReady(false);
      setInvalidReason(reason);
      setInvalidLink(true);
      setCheckingRecovery(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      console.info("[ResetPassword] auth event", {
        event,
        hasSession: Boolean(session),
        userId: session?.user?.id ?? null,
      });

      if (event === "PASSWORD_RECOVERY" && session) {
        markRecoveryReady();
        return;
      }

      if (event === "SIGNED_IN" && session && hasRecoveryContext) {
        markRecoveryReady();
      }
    });

    const checkExistingSession = async () => {
      const code = queryParams.get("code");

      if (code && !hasExchangedCodeRef.current) {
        hasExchangedCodeRef.current = true;

        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code);

          console.info("[ResetPassword] exchangeCodeForSession", {
            ok: !error,
            error: error?.message ?? null,
          });

          if (!error && isMounted) {
            markRecoveryReady();
            return;
          }
        } catch (error) {
          console.error("[ResetPassword] exchange code error", error);
          // Fall through to session check
        }
      }

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        console.info("[ResetPassword] initial session check", {
          hasSession: Boolean(session),
          userId: session?.user?.id ?? null,
        });

        if (session && isMounted) {
          markRecoveryReady();
          return;
        }
      } catch (error) {
        console.error("[ResetPassword] initial session error", error);
        // Keep waiting for auth callback until timeout
      }
    };

    const pollSessionUntilReady = async () => {
      for (let attempt = 0; attempt < 24; attempt += 1) {
        if (!isMounted || hasResolvedRecoveryRef.current) return;

        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();

          console.info("[ResetPassword] polling session", {
            attempt: attempt + 1,
            hasSession: Boolean(session),
          });

          if (session) {
            markRecoveryReady();
            return;
          }
        } catch (error) {
          console.error("[ResetPassword] polling session error", error);
          // Keep polling until timeout
        }

        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    };

    void checkExistingSession();
    void pollSessionUntilReady();

    validationTimeoutRef.current = window.setTimeout(() => {
      markRecoveryInvalid(
        hasRecoveryContext
          ? "Link expirado ou inválido. Solicite um novo."
          : "Link de recuperação inválido. Solicite um novo."
      );
    }, 14000);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearValidationTimeout();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = passwordSchema.safeParse({ password, confirmPassword });
    if (!validation.success) {
      const errors = validation.error.errors.map((err) => err.message).join(". ");
      toast.error(errors);
      return;
    }

    if (isObviouslyWeakPassword(validation.data.password)) {
      toast.error("Essa senha é muito comum e fácil de adivinhar. Escolha uma senha mais forte e única.");
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    console.info("[ResetPassword] before updateUser", {
      hasSession: Boolean(session),
      userId: session?.user?.id ?? null,
    });

    if (!session) {
      const reason = "Sessão de recuperação ausente. Solicite um novo link.";
      toast.error(reason);
      setInvalidReason(reason);
      setInvalidLink(true);
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: validation.data.password,
    });

    setLoading(false);

    if (error) {
      console.error("[ResetPassword] updateUser error", error);
      const rawMsg = (error.message || "").toLowerCase();
      const isSamePassword =
        rawMsg.includes("different from the old password") || rawMsg.includes("same_password");
      const isWeakLeaked =
        rawMsg.includes("pwned") ||
        rawMsg.includes("compromised") ||
        rawMsg.includes("known to be weak") ||
        rawMsg.includes("easy to guess") ||
        rawMsg.includes("weak");

      if (isSamePassword) {
        showWarning(
          "Essa é a sua senha atual",
          "Você digitou a mesma senha que já usa hoje. Para concluir, escolha uma senha diferente da atual — pode ser uma variação, desde que não seja idêntica."
        );
      } else if (isWeakLeaked) {
        showWarning(
          "Escolha uma senha mais segura",
          "Essa senha aparece em vazamentos públicos de dados e não pode ser usada. Combine letras maiúsculas, minúsculas e números em algo que só você saiba."
        );
      } else if (isSessionError(error.message)) {
        const reason = "Sessão expirada ou inválida. Solicite um novo link.";
        toast.error(reason);
        setInvalidReason(reason);
        setInvalidLink(true);
      } else {
        toast.error(mapSupabasePasswordError(error.message));
      }
      return;
    }

    setSuccess(true);
    toast.success("Senha redefinida com sucesso!");
    await supabase.auth.signOut();
    setTimeout(() => navigate("/auth"), 3000);
  };

  // Loading state while validating recovery session
  if (checkingRecovery && !sessionReady && !invalidLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Verificando link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto bg-primary/10 p-3 rounded-full w-fit">
            {success ? (
              <CheckCircle className="h-8 w-8 text-primary" />
            ) : invalidLink ? (
              <AlertCircle className="h-8 w-8 text-destructive" />
            ) : (
              <DollarSign className="h-8 w-8 text-primary" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {success ? "Senha redefinida" : invalidLink ? "Link inválido" : "Nova senha"}
          </CardTitle>
          <CardDescription>
            {success
              ? "Senha redefinida com sucesso! Redirecionando para o login..."
              : invalidLink
              ? invalidReason
              : "Digite sua nova senha"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Ir para login
            </Button>
          ) : invalidLink ? (
            <Button className="w-full" onClick={() => navigate("/forgot-password")}>
              Solicitar novo link
            </Button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {warning && (
                <Alert
                  ref={alertRef}
                  className="border-amber-500/50 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 [&>svg]:text-amber-600"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{warning.title}</AlertTitle>
                  <AlertDescription>{warning.description}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password">Nova senha</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (warning) setWarning(null);
                    }}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <ul className="space-y-1 pt-1">
                  {PASSWORD_REQUIREMENTS.map((req) => {
                    const ok = req.test(password);
                    return (
                      <li
                        key={req.id}
                        className={`flex items-center gap-2 text-xs transition-colors ${
                          ok ? "text-emerald-600" : "text-muted-foreground"
                        }`}
                      >
                        {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        {req.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (warning) setWarning(null);
                    }}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword.length > 0 && confirmPassword !== password && (
                  <p className="text-xs text-destructive">As senhas não coincidem</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Salvando..." : "Salvar nova senha"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
